import { Tooltip } from 'antd';
import { useTranslation } from 'react-i18next';
import {
  Bell as BellIcon,
  Eye as EyeIcon,
  EyeOff as EyeOffIcon,
  LogOut as LogOutIcon,
  Menu as MenuIcon,
  Moon as MoonIcon,
  Sun as SunIcon,
} from 'lucide-react';
import { useNavigate, Link } from 'react-router';
import { useAppDispatch, useAppSelector } from '@/store/hooks';
import { logout } from '@/store/authSlice';
import { toggleTheme } from '@/store/themeSlice';
import { usePrivacyMode } from '@/contexts/PrivacyContext';
import { OrgSwitcher } from './OrgSwitcher';

interface HeaderProps {
  onMenuToggle: () => void;
}

export function Header({ onMenuToggle }: HeaderProps) {
  const { t } = useTranslation();
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const user = useAppSelector((state) => state.auth.user);
  const isDark = useAppSelector((state) => state.theme.isDark);
  const { hidden, togglePrivacy } = usePrivacyMode();
  const myInvitations = useAppSelector((state) => state.organization.myInvitations);
  const pendingSetups = useAppSelector((state) => state.vault.pendingSetups);
  const signingRequestCount = useAppSelector((state) => state.vault.signingRequestCount);
  const notificationCount = myInvitations.length + pendingSetups.length + signingRequestCount;

  const handleLogout = () => {
    dispatch(logout());
    void navigate('/login');
  };

  const handleThemeToggle = () => {
    dispatch(toggleTheme());
  };

  const getInitials = (): string => {
    if (user?.displayName) {
      const parts = user.displayName
        .trim()
        .split(' ')
        .filter((p) => p.length > 0);
      if (parts.length >= 2) {
        const first = parts[0]?.[0] || '';
        const last = parts[parts.length - 1]?.[0] || '';
        return (first + last).toUpperCase();
      }
      return user.displayName.substring(0, 1).toUpperCase();
    }
    if (user?.wkIdentity) {
      // Use last 2 characters since identity always starts with same prefix (bc1q...)
      return user.wkIdentity.slice(-2).toUpperCase();
    }
    if (user?.enterpriseEmail) {
      return user.enterpriseEmail.substring(0, 2).toUpperCase();
    }
    return 'U';
  };

  const getDisplayName = (): string => {
    if (user?.displayName) {
      return user.displayName;
    }
    if (user?.wkIdentity) {
      return `${user.wkIdentity.slice(0, 6)}...${user.wkIdentity.slice(-4)}`;
    }
    if (user?.enterpriseEmail) {
      return user.enterpriseEmail;
    }
    return t('layout.userFallback');
  };

  const getSecondaryInfo = (): string | null => {
    return user?.enterpriseEmail ?? null;
  };

  return (
    <header className="app-header">
      <div className="app-header-left">
        <button className="menu-toggle" onClick={onMenuToggle} aria-label={t('layout.toggleMenu')}>
          <MenuIcon style={{ fontSize: 20 }} />
        </button>
        <Link to="/dashboard" className="app-logo">
          <img
            src={isDark ? '/ssp-logo-white.svg' : '/ssp-logo-black.svg'}
            alt="SSP"
            className="app-logo-img"
          />
          <span className="app-logo-text">SSP Enterprise</span>
        </Link>
        <OrgSwitcher />
      </div>

      <div className="app-header-right">
        {/* Notifications */}
        <Tooltip title={t('layout.notifications')}>
          <button
            className="header-icon-btn"
            aria-label={t('layout.notifications')}
            onClick={() => {
              void navigate('/notifications');
            }}
          >
            <BellIcon />
            {notificationCount > 0 && (
              <span className="header-icon-badge">{notificationCount}</span>
            )}
          </button>
        </Tooltip>

        {/* Privacy Mode Toggle */}
        <Tooltip title={hidden ? t('layout.showBalances') : t('layout.hideBalances')}>
          <button
            className="header-icon-btn"
            aria-label={hidden ? t('layout.showBalances') : t('layout.hideBalances')}
            aria-pressed={hidden}
            onClick={togglePrivacy}
          >
            {hidden ? <EyeOffIcon /> : <EyeIcon />}
          </button>
        </Tooltip>

        {/* Theme Toggle */}
        <Tooltip title={isDark ? t('layout.lightMode') : t('layout.darkMode')}>
          <button
            className="header-icon-btn"
            aria-label={isDark ? t('layout.switchToLight') : t('layout.switchToDark')}
            onClick={handleThemeToggle}
          >
            {isDark ? <SunIcon /> : <MoonIcon />}
          </button>
        </Tooltip>

        {/* Divider */}
        <div className="header-divider" />

        {/* User Section */}
        <button
          className="header-user-section"
          aria-label={t('layout.viewProfile')}
          onClick={() => {
            void navigate('/profile');
          }}
        >
          <div className="user-avatar">{getInitials()}</div>
          <div className="header-user-info">
            <span className="header-user-name">{getDisplayName()}</span>
            {(() => {
              const secondaryInfo = getSecondaryInfo();
              return secondaryInfo && <span className="header-user-email">{secondaryInfo}</span>;
            })()}
          </div>
        </button>

        {/* Logout */}
        <Tooltip title={t('layout.logout')}>
          <button
            className="header-icon-btn"
            aria-label={t('layout.logout')}
            onClick={handleLogout}
          >
            <LogOutIcon />
          </button>
        </Tooltip>
      </div>
    </header>
  );
}

export default Header;
